import { PORTAL_LOGIN_PATH, PORTAL_SET_PASSWORD_PATH } from '@willow/types-iso';

export const FORGOT_PASSWORD_PATH = '/forgot-password';

// better-auth appends the reset token to this path, so the page can tell a reset apart from
// an invite without guessing from the token's shape.
export const SET_PASSWORD_RESET_REDIRECT = `${PORTAL_SET_PASSWORD_PATH}?flow=reset`;

export const AUTH_PATHS: string[] = [PORTAL_LOGIN_PATH, FORGOT_PASSWORD_PATH, PORTAL_SET_PASSWORD_PATH];

// The auth routes are registered without `exact` and without `sensitive`, so React Router also
// renders them for `/login/`, `/login/x` and `/LOGIN`. Matching by exact equality here would let
// those variants through as a returnTo and bounce a borrower who just signed in back to the form.
export const isAuthPath = (pathname: string): boolean => {
  const normalized = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  return AUTH_PATHS.some((path) => normalized === path || normalized.startsWith(`${path}/`));
};

export const safeReturnTo = (value: string | null | undefined): string => {
  if (!value || !value.startsWith('/')) {
    return '/';
  }
  // Parsed rather than string-matched, because this feeds window.location.assign and the WHATWG
  // parser folds a backslash into a slash: `/\evil.example/phish` resolves off-origin.
  let url: URL;
  try {
    url = new URL(value, window.location.origin);
  } catch {
    return '/';
  }
  if (url.origin !== window.location.origin) {
    return '/';
  }
  // The URL parser leaves the pathname percent-encoded, but history v4 decodes it before React
  // Router matches, so an undecoded comparison would let `/%6Cogin` through to the login page.
  // Decoding is for the comparison only: the value returned stays encoded so that history's own
  // decode is still the first and only one. A malformed sequence is rejected rather than cleaned
  // up, because history throws a URIError on it and would take the router down on arrival.
  let decodedPathname: string;
  try {
    decodedPathname = decodeURIComponent(url.pathname);
  } catch {
    return '/';
  }
  if (isAuthPath(decodedPathname)) {
    return '/';
  }
  return `${url.pathname}${url.search}${url.hash}`;
};
